import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleCheckIcon, CircleMinusIcon, CircleXIcon } from '@persona-id/design-tokens/icons';
import dompurify from 'dompurify';
import { filter, isEmpty, isEqual, negate, pipe, reject, sortBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import DeviceIcon from 'dashboard/components/DeviceIcon';
import UpdatedFieldsEventDescription from 'dashboard/components/EventTimeline/UpdatedFieldsEventDescription';
import { VERIFICATION_TYPE_NAMES } from 'dashboard/lib/constants/verification';
import { getTimeElapsed } from 'dashboard/lib/utils/getTimeElapsed';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { AccountEvent } from 'dashboard/models/account';
import { CaseEvent } from 'dashboard/models/case';
import { CaseAssignmentPolicy } from 'dashboard/models/case-assignment-policy';
import { CaseMessage } from 'dashboard/models/case-message';
import { Device } from 'dashboard/models/device';
import { Document, DocumentStatus } from 'dashboard/models/document';
import { Event } from 'dashboard/models/event';
import { InquiryEvent } from 'dashboard/models/inquiry';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { TransactionEvent } from 'dashboard/models/transaction';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import { WorkflowRun, WorkflowRunEvent, WorkflowRunType } from 'dashboard/models/workflow-run';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import { Label } from 'design-system/components/Text';
import ImageDot from 'images/dashboard/dot.svg';
import { EVENT_NAME_TO_DESCRIPTION, EventName } from 'lib/constants/events';
import { MINUTES_SECONDS_ELAPSED_FORMAT } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { assertNever } from 'lib/utils/types';
import { VerificationStatus } from 'lib/verification';

const EventTimelinesContainer = styled('div')`
  padding-bottom: ${Spacing[3]};
`;

export type EventTimelinePage = 'inquiry' | 'case' | 'transaction' | 'account';

interface EventTimelineProps {
  caseAssignmentPolicies: CaseAssignmentPolicy[];
  importerPipelineMap: Record<string, { id: string; name: string }>;
  events: Event[];
  workflowIdToNameMap: Record<string, string>;
  workflowRuns: WorkflowRun[];
  users: User[];
  location: EventTimelinePage;
}

const EventTimelines = ({
  caseAssignmentPolicies,
  importerPipelineMap,
  events,
  workflowIdToNameMap,
  location,
  workflowRuns,
  users,
}: EventTimelineProps) => {
  // Avoid displaying any events related to government ID documents since current users are only
  // looking at government ID verifications in Dashboard.
  events = reject(
    (event) => event.attributes.payload.data.type === ApiModel.DocumentGovernmentId,
    events,
  );

  // If we can find case.status-update events that have the same content as a case.resolved or case.reopened event,
  // we filter out the respective status-update events as we only want to show the resolved/reopened events to prevent duplicated information.
  events = events.filter((event) => {
    if (event.attributes.name === EventName.CaseStatusUpdated) {
      const matchingCaseResolvedOrReopenedEvent = events.find(
        (e) =>
          (e.attributes.name === EventName.CaseResolved &&
            isEqual(e.attributes.payload, event.attributes.payload)) ||
          (e.attributes.name === EventName.CaseReopened &&
            isEqual(e.attributes.payload, event.attributes.payload)),
      );
      return !matchingCaseResolvedOrReopenedEvent;
    }
    return true;
  });

  events = sortBy((event) => event.attributes['created-at'], events).reverse();

  const permissionMap = {
    hasWorkflowRunPermission: usePermission(Permission.WorkflowRunList),
    hasWorkflowViewPermission: usePermission(Permission.WorkflowView),
  };

  const permissibleEvents = events.filter((event) => permissibleEvent(event, permissionMap));
  const supportedEvents = permissibleEvents.filter((event) => supportedEvent(event, location));
  const daySegments = useMemo(() => getDaySegmentedEventTimelines()(supportedEvents), [events]);

  if (events.length === 0) {
    return (
      <EventTimelineContainer role='log'>
        <Card.Body>{`No events have occurred yet for this ${location}`}.</Card.Body>
      </EventTimelineContainer>
    );
  }

  return (
    <EventTimelinesContainer role='log'>
      {daySegments.map((s, i) => (
        <EventTimelineByDay
          key={i}
          caseAssignmentPolicies={caseAssignmentPolicies}
          importerPipelineMap={importerPipelineMap}
          events={s}
          workflowIdToNameMap={workflowIdToNameMap}
          workflowRuns={workflowRuns}
          users={users}
          location={location}
        />
      ))}
    </EventTimelinesContainer>
  );
};

export default EventTimelines;

/**
 * Returns true if the event is supported in the Timeline component.
 */
/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const supportedEvent = (event: Event, location: EventTimelinePage): boolean => {
  if (location === 'case') {
    switch (event.attributes.name) {
      case EventName.AccountCreated:
      case EventName.AccountStatusUpdated:
      case EventName.CaseCreated:
      case EventName.CaseAssigned:
      case EventName.CaseUpdated:
      case EventName.CaseResolved:
      case EventName.CaseReopened:
      case EventName.CaseStatusUpdated:
      case EventName.CaseMessageSent:
      case EventName.CaseMessageReceived:
      case EventName.DocumentCreated:
      case EventName.DocumentSubmitted:
      case EventName.DocumentProcessed:
      case EventName.DocumentExtracted:
      case EventName.DocumentErrored:
      case EventName.ExternalIntegrationLogCreated:
      case EventName.InquiryCreated:
      case EventName.InquirySessionStarted:
      case EventName.InquiryCompleted:
      case EventName.InquiryDeclined:
      case EventName.InquiryApproved:
      case EventName.InquiryMarkedForReview:
      case EventName.InquiryExpired:
      case EventName.VerificationCreated:
      case EventName.VerificationPassed:
      case EventName.VerificationFailed:
      case EventName.VerificationCanceled:
      case EventName.VerificationTentativelyPassed:
      case EventName.VerificationTentativelyFailed:
      case EventName.ReportAddressLookupErrored:
      case EventName.ReportAddressLookupReady:
      case EventName.ReportAdverseMediaDismissed:
      case EventName.ReportAdverseMediaErrored:
      case EventName.ReportAdverseMediaMatched:
      case EventName.ReportAdverseMediaReady:
      case EventName.ReportBusinessAdverseMediaDismissed:
      case EventName.ReportBusinessAdverseMediaErrored:
      case EventName.ReportBusinessAdverseMediaMatched:
      case EventName.ReportBusinessAdverseMediaReady:
      case EventName.ReportBusinessAssociatedPersonsErrored:
      case EventName.ReportBusinessAssociatedPersonsMatched:
      case EventName.ReportBusinessAssociatedPersonsReady:
      case EventName.ReportBusinessClassificationErrored:
      case EventName.ReportBusinessClassificationReady:
      case EventName.ReportBusinessIndustryClassificationErrored:
      case EventName.ReportBusinessIndustryClassificationReady:
      case EventName.ReportBusinessIndustryClassificationSubmitted:
      case EventName.ReportBusinessLiensLookupErrored:
      case EventName.ReportBusinessLiensLookupReady:
      case EventName.ReportBusinessLookupErrored:
      case EventName.ReportBusinessLookupReady:
      case EventName.ReportBusinessNonprofitChanged:
      case EventName.ReportBusinessNonprofitErrored:
      case EventName.ReportBusinessNonprofitReady:
      case EventName.ReportBusinessOnlinePresenceErrored:
      case EventName.ReportBusinessOnlinePresenceReady:
      case EventName.ReportBusinessOnlinePresenceSubmitted:
      case EventName.ReportBusinessRegistrationsLookupReady:
      case EventName.ReportBusinessRegistrationsLookupErrored:
      case EventName.ReportBusinessSocialMediaErrored:
      case EventName.ReportBusinessSocialMediaReady:
      case EventName.ReportBusinessWatchlistDismissed:
      case EventName.ReportBusinessWatchlistErrored:
      case EventName.ReportBusinessWatchlistMatched:
      case EventName.ReportBusinessWatchlistReady:
      case EventName.ReportBusinessWebsiteErrored:
      case EventName.ReportBusinessWebsiteReady:
      case EventName.ReportClearbitBusinessLookupErrored:
      case EventName.ReportClearbitBusinessLookupReady:
      case EventName.ReportCryptoAddressWatchlistErrored:
      case EventName.ReportCryptoAddressWatchlistMatched:
      case EventName.ReportCryptoAddressWatchlistReady:
      case EventName.ReportCustomListDismissed:
      case EventName.ReportCustomListErrored:
      case EventName.ReportCustomListMatched:
      case EventName.ReportCustomListReady:
      case EventName.ReportEmailAddressErrored:
      case EventName.ReportEmailAddressReady:
      case EventName.ReportFinraBrokerCheckErrored:
      case EventName.ReportFinraBrokerCheckReady:
      case EventName.ReportKyckrBusinessLookupErrored:
      case EventName.ReportKyckrBusinessLookupReady:
      case EventName.ReportMiddeskErrored:
      case EventName.ReportMiddeskReady:
      case EventName.ReportMxAccountErrored:
      case EventName.ReportMxAccountReady:
      case EventName.ReportPhoneNumberErrored:
      case EventName.ReportPhoneNumberReady:
      case EventName.ReportPoliticallyExposedPersonDismissed:
      case EventName.ReportPoliticallyExposedPersonErrored:
      case EventName.ReportPoliticallyExposedPersonMatched:
      case EventName.ReportPoliticallyExposedPersonReady:
      case EventName.ReportProfileErrored:
      case EventName.ReportProfileReady:
      case EventName.ReportProfileNonAuthoritativeErrored:
      case EventName.ReportProfileNonAuthoritativeReady:
      case EventName.ReportScreeningErrored:
      case EventName.ReportScreeningReady:
      case EventName.ReportSecActionLookupErrored:
      case EventName.ReportSecActionLookupReady:
      case EventName.ReportSentilinkApplicationRiskErrored:
      case EventName.ReportSentilinkApplicationRiskReady:
      case EventName.ReportSentilinkScoresErrored:
      case EventName.ReportSentilinkScoresReady:
      case EventName.ReportSocialMediaErrored:
      case EventName.ReportSocialMediaReady:
      case EventName.ReportSyntheticErrored:
      case EventName.ReportSyntheticReady:
      case EventName.ReportWatchlistDismissed:
      case EventName.ReportWatchlistErrored:
      case EventName.ReportWatchlistMatched:
      case EventName.ReportWatchlistReady:
      case EventName.SARCreated:
      case EventName.SARFiled:
      case EventName.TransactionCreated:
      case EventName.TransactionLabeled:
      case EventName.TransactionRedacted:
      case EventName.TransactionStatusUpdated:
      case EventName.WorkflowRunCreated:
      case EventName.WorkflowRunErrored:
      case EventName.WorkflowRunResumed:
        return true;
      case EventName.AccountRedacted:
      case EventName.AccountArchived:
      case EventName.AccountRestored:
      case EventName.AccountConsolidated:
      case EventName.AccountTagAdded:
      case EventName.AccountTagRemoved:
      case EventName.AccountUpdatedFields:
      case EventName.CaseSlaExpired:
      case EventName.CaseQueueUpdated:
      case EventName.GraphQueryCancelled:
      case EventName.GraphQueryCompleted:
      case EventName.GraphQueryErrored:
      case EventName.GraphQueryReran:
      case EventName.GraphQuerySubmitted:
      case EventName.GraphQueryTimedOut:
      case EventName.InquirySessionExpired:
      case EventName.InquirySessionCanceled:
      case EventName.InquiryStarted:
      case EventName.InquiryFailed:
      case EventName.InquiryTransitioned:
      case EventName.InquiryRedacted:
      case EventName.InquiryReusablePersonaCreated:
      case EventName.InquiryReusablePersonaUsed:
      case EventName.InquiryRunConditionalRouted:
      case EventName.InquiryTemplateVersionCreated:
      case EventName.InquiryTemplateVersionPublished:
      case EventName.SelfieCreated:
      case EventName.SelfieSubmitted:
      case EventName.SelfieProcessed:
      case EventName.SelfieErrored:
      case EventName.SessionCreated:
      case EventName.SessionExpired:
      case EventName.VerificationSubmitted:
      case EventName.VerificationRequiresRetry:
        return false;
      default:
        assertNever(event.attributes.name);
    }
  } else if (location === 'inquiry') {
    switch (event.attributes.name) {
      case EventName.AccountCreated:
      case EventName.AccountStatusUpdated:
      case EventName.CaseCreated:
      case EventName.CaseAssigned:
      case EventName.CaseUpdated:
      case EventName.CaseResolved:
      case EventName.CaseReopened:
      case EventName.CaseStatusUpdated:
      case EventName.DocumentCreated:
      case EventName.DocumentSubmitted:
      case EventName.DocumentExtracted:
      case EventName.DocumentProcessed:
      case EventName.DocumentErrored:
      case EventName.ExternalIntegrationLogCreated:
      case EventName.InquiryCreated:
      case EventName.InquirySessionStarted:
      case EventName.InquiryCompleted:
      case EventName.InquiryDeclined:
      case EventName.InquiryApproved:
      case EventName.InquiryMarkedForReview:
      case EventName.InquiryExpired:
      case EventName.InquiryRedacted:
      case EventName.TransactionCreated:
      case EventName.TransactionLabeled:
      case EventName.TransactionRedacted:
      case EventName.TransactionStatusUpdated:
      case EventName.VerificationCreated:
      case EventName.VerificationSubmitted:
      case EventName.VerificationPassed:
      case EventName.VerificationRequiresRetry:
      case EventName.VerificationFailed:
      case EventName.VerificationCanceled:
      case EventName.VerificationTentativelyPassed:
      case EventName.VerificationTentativelyFailed:
      case EventName.WorkflowRunCreated:
      case EventName.WorkflowRunErrored:
      case EventName.WorkflowRunResumed:
        return true;
      case EventName.AccountRedacted:
      case EventName.AccountArchived:
      case EventName.AccountRestored:
      case EventName.AccountConsolidated:
      case EventName.AccountTagAdded:
      case EventName.AccountTagRemoved:
      case EventName.AccountUpdatedFields:
      case EventName.CaseMessageSent:
      case EventName.CaseMessageReceived:
      case EventName.CaseQueueUpdated:
      case EventName.CaseSlaExpired:
      case EventName.GraphQueryCancelled:
      case EventName.GraphQueryCompleted:
      case EventName.GraphQueryErrored:
      case EventName.GraphQueryReran:
      case EventName.GraphQuerySubmitted:
      case EventName.GraphQueryTimedOut:
      case EventName.InquiryReusablePersonaCreated:
      case EventName.InquiryReusablePersonaUsed:
      case EventName.InquirySessionExpired:
      case EventName.InquirySessionCanceled:
      case EventName.InquiryStarted:
      case EventName.InquiryFailed:
      case EventName.InquiryTransitioned:
      case EventName.InquiryRunConditionalRouted:
      case EventName.InquiryTemplateVersionCreated:
      case EventName.InquiryTemplateVersionPublished:
      case EventName.ReportAddressLookupErrored:
      case EventName.ReportAddressLookupReady:
      case EventName.ReportAdverseMediaDismissed:
      case EventName.ReportAdverseMediaErrored:
      case EventName.ReportAdverseMediaMatched:
      case EventName.ReportAdverseMediaReady:
      case EventName.ReportBusinessClassificationErrored:
      case EventName.ReportBusinessClassificationReady:
      case EventName.ReportBusinessIndustryClassificationErrored:
      case EventName.ReportBusinessIndustryClassificationReady:
      case EventName.ReportBusinessIndustryClassificationSubmitted:
      case EventName.ReportBusinessAdverseMediaDismissed:
      case EventName.ReportBusinessAdverseMediaErrored:
      case EventName.ReportBusinessAdverseMediaMatched:
      case EventName.ReportBusinessAdverseMediaReady:
      case EventName.ReportBusinessAssociatedPersonsErrored:
      case EventName.ReportBusinessAssociatedPersonsMatched:
      case EventName.ReportBusinessAssociatedPersonsReady:
      case EventName.ReportBusinessLiensLookupErrored:
      case EventName.ReportBusinessLiensLookupReady:
      case EventName.ReportBusinessLookupErrored:
      case EventName.ReportBusinessLookupReady:
      case EventName.ReportBusinessNonprofitChanged:
      case EventName.ReportBusinessNonprofitErrored:
      case EventName.ReportBusinessNonprofitReady:
      case EventName.ReportBusinessOnlinePresenceErrored:
      case EventName.ReportBusinessOnlinePresenceReady:
      case EventName.ReportBusinessOnlinePresenceSubmitted:
      case EventName.ReportBusinessRegistrationsLookupErrored:
      case EventName.ReportBusinessRegistrationsLookupReady:
      case EventName.ReportBusinessSocialMediaErrored:
      case EventName.ReportBusinessSocialMediaReady:
      case EventName.ReportBusinessWatchlistDismissed:
      case EventName.ReportBusinessWatchlistErrored:
      case EventName.ReportBusinessWatchlistMatched:
      case EventName.ReportBusinessWatchlistReady:
      case EventName.ReportBusinessWebsiteErrored:
      case EventName.ReportBusinessWebsiteReady:
      case EventName.ReportClearbitBusinessLookupErrored:
      case EventName.ReportClearbitBusinessLookupReady:
      case EventName.ReportCryptoAddressWatchlistErrored:
      case EventName.ReportCryptoAddressWatchlistMatched:
      case EventName.ReportCryptoAddressWatchlistReady:
      case EventName.ReportCustomListDismissed:
      case EventName.ReportCustomListErrored:
      case EventName.ReportCustomListMatched:
      case EventName.ReportCustomListReady:
      case EventName.ReportEmailAddressErrored:
      case EventName.ReportEmailAddressReady:
      case EventName.ReportFinraBrokerCheckErrored:
      case EventName.ReportFinraBrokerCheckReady:
      case EventName.ReportKyckrBusinessLookupErrored:
      case EventName.ReportKyckrBusinessLookupReady:
      case EventName.ReportPhoneNumberErrored:
      case EventName.ReportPhoneNumberReady:
      case EventName.ReportPoliticallyExposedPersonDismissed:
      case EventName.ReportPoliticallyExposedPersonErrored:
      case EventName.ReportPoliticallyExposedPersonMatched:
      case EventName.ReportPoliticallyExposedPersonReady:
      case EventName.ReportProfileErrored:
      case EventName.ReportProfileReady:
      case EventName.ReportProfileNonAuthoritativeErrored:
      case EventName.ReportProfileNonAuthoritativeReady:
      case EventName.ReportMiddeskErrored:
      case EventName.ReportMiddeskReady:
      case EventName.ReportMxAccountErrored:
      case EventName.ReportMxAccountReady:
      case EventName.ReportSecActionLookupErrored:
      case EventName.ReportSecActionLookupReady:
      case EventName.ReportSentilinkApplicationRiskErrored:
      case EventName.ReportSentilinkApplicationRiskReady:
      case EventName.ReportSentilinkScoresErrored:
      case EventName.ReportSentilinkScoresReady:
      case EventName.ReportSocialMediaErrored:
      case EventName.ReportSocialMediaReady:
      case EventName.ReportSyntheticErrored:
      case EventName.ReportSyntheticReady:
      case EventName.ReportScreeningErrored:
      case EventName.ReportScreeningReady:
      case EventName.ReportWatchlistDismissed:
      case EventName.ReportWatchlistErrored:
      case EventName.ReportWatchlistMatched:
      case EventName.ReportWatchlistReady:
      case EventName.SARCreated:
      case EventName.SARFiled:
      case EventName.SelfieCreated:
      case EventName.SelfieSubmitted:
      case EventName.SelfieProcessed:
      case EventName.SelfieErrored:
      case EventName.SessionCreated:
      case EventName.SessionExpired:
        return false;
      default:
        assertNever(event.attributes.name);
    }
  } else if (location === 'transaction') {
    switch (event.attributes.name) {
      case EventName.AccountStatusUpdated:
      case EventName.CaseCreated:
      case EventName.CaseAssigned:
      case EventName.CaseUpdated:
      case EventName.CaseResolved:
      case EventName.CaseReopened:
      case EventName.CaseStatusUpdated:
      case EventName.DocumentCreated:
      case EventName.DocumentSubmitted:
      case EventName.DocumentExtracted:
      case EventName.DocumentProcessed:
      case EventName.DocumentErrored:
      case EventName.ExternalIntegrationLogCreated:
      case EventName.InquiryCreated:
      case EventName.InquirySessionStarted:
      case EventName.InquiryCompleted:
      case EventName.InquiryDeclined:
      case EventName.InquiryApproved:
      case EventName.InquiryMarkedForReview:
      case EventName.InquiryExpired:
      case EventName.TransactionCreated:
      case EventName.TransactionLabeled:
      case EventName.TransactionStatusUpdated:
      case EventName.TransactionRedacted:
      case EventName.VerificationCreated:
      case EventName.VerificationSubmitted:
      case EventName.VerificationPassed:
      case EventName.VerificationRequiresRetry:
      case EventName.VerificationFailed:
      case EventName.VerificationCanceled:
      case EventName.VerificationTentativelyPassed:
      case EventName.VerificationTentativelyFailed:
      case EventName.WorkflowRunCreated:
      case EventName.WorkflowRunErrored:
      case EventName.WorkflowRunResumed:
      case EventName.AccountCreated:
      case EventName.AccountRedacted:
      case EventName.AccountArchived:
      case EventName.AccountRestored:
      case EventName.AccountConsolidated:
      case EventName.AccountTagAdded:
      case EventName.AccountTagRemoved:
      case EventName.GraphQueryCancelled:
      case EventName.GraphQueryCompleted:
      case EventName.GraphQueryErrored:
      case EventName.GraphQuerySubmitted:
      case EventName.GraphQueryReran:
      case EventName.GraphQueryTimedOut:
      case EventName.InquirySessionExpired:
      case EventName.InquirySessionCanceled:
      case EventName.InquiryStarted:
      case EventName.InquiryFailed:
      case EventName.InquiryTransitioned:
      case EventName.ReportAddressLookupErrored:
      case EventName.ReportAddressLookupReady:
      case EventName.ReportAdverseMediaDismissed:
      case EventName.ReportAdverseMediaErrored:
      case EventName.ReportAdverseMediaMatched:
      case EventName.ReportAdverseMediaReady:
      case EventName.ReportBusinessAdverseMediaDismissed:
      case EventName.ReportBusinessAdverseMediaErrored:
      case EventName.ReportBusinessAdverseMediaMatched:
      case EventName.ReportBusinessAdverseMediaReady:
      case EventName.ReportBusinessAssociatedPersonsErrored:
      case EventName.ReportBusinessAssociatedPersonsMatched:
      case EventName.ReportBusinessAssociatedPersonsReady:
      case EventName.ReportBusinessClassificationErrored:
      case EventName.ReportBusinessClassificationReady:
      case EventName.ReportBusinessIndustryClassificationErrored:
      case EventName.ReportBusinessIndustryClassificationReady:
      case EventName.ReportBusinessIndustryClassificationSubmitted:
      case EventName.ReportBusinessLiensLookupErrored:
      case EventName.ReportBusinessLiensLookupReady:
      case EventName.ReportBusinessLookupErrored:
      case EventName.ReportBusinessLookupReady:
      case EventName.ReportBusinessNonprofitChanged:
      case EventName.ReportBusinessNonprofitErrored:
      case EventName.ReportBusinessNonprofitReady:
      case EventName.ReportBusinessOnlinePresenceErrored:
      case EventName.ReportBusinessOnlinePresenceReady:
      case EventName.ReportBusinessOnlinePresenceSubmitted:
      case EventName.ReportBusinessRegistrationsLookupErrored:
      case EventName.ReportBusinessRegistrationsLookupReady:
      case EventName.ReportBusinessSocialMediaErrored:
      case EventName.ReportBusinessSocialMediaReady:
      case EventName.ReportBusinessWatchlistDismissed:
      case EventName.ReportBusinessWatchlistErrored:
      case EventName.ReportBusinessWatchlistMatched:
      case EventName.ReportBusinessWatchlistReady:
      case EventName.ReportBusinessWebsiteErrored:
      case EventName.ReportBusinessWebsiteReady:
      case EventName.ReportClearbitBusinessLookupErrored:
      case EventName.ReportClearbitBusinessLookupReady:
      case EventName.ReportCryptoAddressWatchlistErrored:
      case EventName.ReportCryptoAddressWatchlistMatched:
      case EventName.ReportCryptoAddressWatchlistReady:
      case EventName.ReportCustomListDismissed:
      case EventName.ReportCustomListErrored:
      case EventName.ReportCustomListMatched:
      case EventName.ReportCustomListReady:
      case EventName.ReportEmailAddressErrored:
      case EventName.ReportEmailAddressReady:
      case EventName.ReportFinraBrokerCheckErrored:
      case EventName.ReportFinraBrokerCheckReady:
      case EventName.ReportKyckrBusinessLookupErrored:
      case EventName.ReportKyckrBusinessLookupReady:
      case EventName.ReportMiddeskErrored:
      case EventName.ReportMiddeskReady:
      case EventName.ReportMxAccountErrored:
      case EventName.ReportMxAccountReady:
      case EventName.ReportPhoneNumberErrored:
      case EventName.ReportPhoneNumberReady:
      case EventName.ReportPoliticallyExposedPersonDismissed:
      case EventName.ReportPoliticallyExposedPersonErrored:
      case EventName.ReportPoliticallyExposedPersonMatched:
      case EventName.ReportPoliticallyExposedPersonReady:
      case EventName.ReportProfileErrored:
      case EventName.ReportProfileReady:
      case EventName.ReportProfileNonAuthoritativeErrored:
      case EventName.ReportProfileNonAuthoritativeReady:
      case EventName.ReportScreeningErrored:
      case EventName.ReportScreeningReady:
      case EventName.ReportSecActionLookupErrored:
      case EventName.ReportSecActionLookupReady:
      case EventName.ReportSentilinkApplicationRiskErrored:
      case EventName.ReportSentilinkApplicationRiskReady:
      case EventName.ReportSentilinkScoresReady:
      case EventName.ReportSentilinkScoresErrored:
      case EventName.ReportSocialMediaErrored:
      case EventName.ReportSocialMediaReady:
      case EventName.ReportSyntheticErrored:
      case EventName.ReportSyntheticReady:
      case EventName.ReportWatchlistDismissed:
      case EventName.ReportWatchlistErrored:
      case EventName.ReportWatchlistMatched:
      case EventName.ReportWatchlistReady:
      case EventName.SARCreated:
      case EventName.SARFiled:
      case EventName.SelfieCreated:
      case EventName.SelfieSubmitted:
      case EventName.SelfieProcessed:
      case EventName.SelfieErrored:
      case EventName.SessionCreated:
      case EventName.SessionExpired:
        return true;
      case EventName.AccountUpdatedFields:
      case EventName.CaseMessageSent:
      case EventName.CaseMessageReceived:
      case EventName.CaseQueueUpdated:
      case EventName.CaseSlaExpired:
      case EventName.InquiryRunConditionalRouted:
      case EventName.InquiryRedacted:
      case EventName.InquiryReusablePersonaCreated:
      case EventName.InquiryReusablePersonaUsed:
      case EventName.InquiryTemplateVersionCreated:
      case EventName.InquiryTemplateVersionPublished:
        return false;
      default:
        assertNever(event.attributes.name);
    }
  } else if (location === 'account') {
    switch (event.attributes.name) {
      case EventName.AccountArchived:
      case EventName.AccountConsolidated:
      case EventName.AccountCreated:
      case EventName.AccountRedacted:
      case EventName.AccountRestored:
      case EventName.AccountStatusUpdated:
      case EventName.AccountTagAdded:
      case EventName.AccountTagRemoved:
      case EventName.AccountUpdatedFields:
        return true;
      case EventName.CaseAssigned:
      case EventName.CaseCreated:
      case EventName.CaseMessageReceived:
      case EventName.CaseMessageSent:
      case EventName.CaseQueueUpdated:
      case EventName.CaseReopened:
      case EventName.CaseResolved:
      case EventName.CaseSlaExpired:
      case EventName.CaseStatusUpdated:
      case EventName.CaseUpdated:
      case EventName.DocumentCreated:
      case EventName.DocumentErrored:
      case EventName.DocumentExtracted:
      case EventName.DocumentProcessed:
      case EventName.DocumentSubmitted:
      case EventName.ExternalIntegrationLogCreated:
      case EventName.GraphQueryCancelled:
      case EventName.GraphQueryCompleted:
      case EventName.GraphQueryErrored:
      case EventName.GraphQueryReran:
      case EventName.GraphQuerySubmitted:
      case EventName.GraphQueryTimedOut:
      case EventName.InquiryApproved:
      case EventName.InquiryCompleted:
      case EventName.InquiryCreated:
      case EventName.InquiryDeclined:
      case EventName.InquiryExpired:
      case EventName.InquiryFailed:
      case EventName.InquiryMarkedForReview:
      case EventName.InquiryRedacted:
      case EventName.InquiryReusablePersonaCreated:
      case EventName.InquiryReusablePersonaUsed:
      case EventName.InquiryRunConditionalRouted:
      case EventName.InquirySessionCanceled:
      case EventName.InquirySessionExpired:
      case EventName.InquirySessionStarted:
      case EventName.InquiryStarted:
      case EventName.InquiryTemplateVersionCreated:
      case EventName.InquiryTemplateVersionPublished:
      case EventName.InquiryTransitioned:
      case EventName.ReportAddressLookupErrored:
      case EventName.ReportAddressLookupReady:
      case EventName.ReportAdverseMediaDismissed:
      case EventName.ReportAdverseMediaErrored:
      case EventName.ReportAdverseMediaMatched:
      case EventName.ReportAdverseMediaReady:
      case EventName.ReportBusinessAdverseMediaDismissed:
      case EventName.ReportBusinessAdverseMediaErrored:
      case EventName.ReportBusinessAdverseMediaMatched:
      case EventName.ReportBusinessAdverseMediaReady:
      case EventName.ReportBusinessAssociatedPersonsErrored:
      case EventName.ReportBusinessAssociatedPersonsMatched:
      case EventName.ReportBusinessAssociatedPersonsReady:
      case EventName.ReportBusinessClassificationErrored:
      case EventName.ReportBusinessClassificationReady:
      case EventName.ReportBusinessIndustryClassificationErrored:
      case EventName.ReportBusinessIndustryClassificationReady:
      case EventName.ReportBusinessIndustryClassificationSubmitted:
      case EventName.ReportBusinessLiensLookupErrored:
      case EventName.ReportBusinessLiensLookupReady:
      case EventName.ReportBusinessLookupErrored:
      case EventName.ReportBusinessLookupReady:
      case EventName.ReportBusinessNonprofitChanged:
      case EventName.ReportBusinessNonprofitErrored:
      case EventName.ReportBusinessNonprofitReady:
      case EventName.ReportBusinessOnlinePresenceErrored:
      case EventName.ReportBusinessOnlinePresenceReady:
      case EventName.ReportBusinessOnlinePresenceSubmitted:
      case EventName.ReportBusinessRegistrationsLookupErrored:
      case EventName.ReportBusinessRegistrationsLookupReady:
      case EventName.ReportBusinessSocialMediaErrored:
      case EventName.ReportBusinessSocialMediaReady:
      case EventName.ReportBusinessWatchlistDismissed:
      case EventName.ReportBusinessWatchlistErrored:
      case EventName.ReportBusinessWatchlistMatched:
      case EventName.ReportBusinessWatchlistReady:
      case EventName.ReportBusinessWebsiteErrored:
      case EventName.ReportBusinessWebsiteReady:
      case EventName.ReportClearbitBusinessLookupErrored:
      case EventName.ReportClearbitBusinessLookupReady:
      case EventName.ReportCryptoAddressWatchlistErrored:
      case EventName.ReportCryptoAddressWatchlistMatched:
      case EventName.ReportCryptoAddressWatchlistReady:
      case EventName.ReportCustomListDismissed:
      case EventName.ReportCustomListErrored:
      case EventName.ReportCustomListMatched:
      case EventName.ReportCustomListReady:
      case EventName.ReportEmailAddressErrored:
      case EventName.ReportEmailAddressReady:
      case EventName.ReportFinraBrokerCheckErrored:
      case EventName.ReportFinraBrokerCheckReady:
      case EventName.ReportKyckrBusinessLookupErrored:
      case EventName.ReportKyckrBusinessLookupReady:
      case EventName.ReportMiddeskErrored:
      case EventName.ReportMiddeskReady:
      case EventName.ReportMxAccountErrored:
      case EventName.ReportMxAccountReady:
      case EventName.ReportPhoneNumberErrored:
      case EventName.ReportPhoneNumberReady:
      case EventName.ReportPoliticallyExposedPersonDismissed:
      case EventName.ReportPoliticallyExposedPersonErrored:
      case EventName.ReportPoliticallyExposedPersonMatched:
      case EventName.ReportPoliticallyExposedPersonReady:
      case EventName.ReportProfileErrored:
      case EventName.ReportProfileReady:
      case EventName.ReportProfileNonAuthoritativeErrored:
      case EventName.ReportProfileNonAuthoritativeReady:
      case EventName.ReportScreeningErrored:
      case EventName.ReportScreeningReady:
      case EventName.ReportSecActionLookupErrored:
      case EventName.ReportSecActionLookupReady:
      case EventName.ReportSentilinkApplicationRiskErrored:
      case EventName.ReportSentilinkApplicationRiskReady:
      case EventName.ReportSentilinkScoresErrored:
      case EventName.ReportSentilinkScoresReady:
      case EventName.ReportSocialMediaErrored:
      case EventName.ReportSocialMediaReady:
      case EventName.ReportSyntheticErrored:
      case EventName.ReportSyntheticReady:
      case EventName.ReportWatchlistDismissed:
      case EventName.ReportWatchlistErrored:
      case EventName.ReportWatchlistMatched:
      case EventName.ReportWatchlistReady:
      case EventName.SARCreated:
      case EventName.SARFiled:
      case EventName.SelfieCreated:
      case EventName.SelfieErrored:
      case EventName.SelfieProcessed:
      case EventName.SelfieSubmitted:
      case EventName.SessionCreated:
      case EventName.SessionExpired:
      case EventName.TransactionCreated:
      case EventName.TransactionLabeled:
      case EventName.TransactionRedacted:
      case EventName.TransactionStatusUpdated:
      case EventName.VerificationCanceled:
      case EventName.VerificationCreated:
      case EventName.VerificationFailed:
      case EventName.VerificationPassed:
      case EventName.VerificationRequiresRetry:
      case EventName.VerificationSubmitted:
      case EventName.VerificationTentativelyFailed:
      case EventName.VerificationTentativelyPassed:
      case EventName.WorkflowRunCreated:
      case EventName.WorkflowRunErrored:
      case EventName.WorkflowRunResumed:
        return false;
      default:
        assertNever(event.attributes.name);
    }
  }
};

const permissibleEvent = (
  event: Event,
  permissionMap: { hasWorkflowRunPermission: boolean; hasWorkflowViewPermission: boolean },
): boolean => {
  switch (event.attributes.name) {
    case EventName.WorkflowRunCreated:
      // We cannot show WorkflowRunCreated events without WorkflowRun and WorkflowView permissions because
      // we rely on information in the WorkflowRun and Workflow to display the event.
      return permissionMap.hasWorkflowRunPermission && permissionMap.hasWorkflowViewPermission;
    default:
      return true;
  }
};

/**
 * Helper function to split a list of events by the `'inquiry-session.started'`
 * event, which indicates a new "segment" of the event timeline.
 */
const splitEventsByDeviceChange = (
  [head, ...tail]: Event[],
  currSegment: Event[] = [],
  allSegments: Event[][] = [],
): Event[][] => {
  if (head == null) {
    return allSegments.concat([currSegment]);
  }

  return head.attributes.name === EventName.InquirySessionStarted
    ? splitEventsByDeviceChange(tail, [head], allSegments.concat([currSegment]))
    : splitEventsByDeviceChange(tail, currSegment.concat(head), allSegments);
};

/**
 * Helper function to split a list of events by date so that we can display
 * when the day changes.
 */
const splitEventsByDay = (
  [head, ...tail]: Event[],
  currSegment: Event[] = [],
  allSegments: Event[][] = [],
): Event[][] => {
  if (head == null) {
    return allSegments.concat([currSegment]);
  }

  const lastEvent = currSegment[currSegment.length - 1];

  return lastEvent &&
    DateTime.fromISO(lastEvent.attributes['created-at']).startOf('day').toISO() !==
      DateTime.fromISO(head.attributes['created-at']).startOf('day').toISO()
    ? splitEventsByDay(tail, [head], allSegments.concat([currSegment]))
    : splitEventsByDay(tail, currSegment.concat(head), allSegments);
};

/**
 * Helper that does several things:
 * 1. subdivide them into a list of lists, split by the device change event
 * 2. filter out empty lists
 * 3. filter out lists that would be empty except for device
 */
export const getDeviceSegmentedEventTimelines = () =>
  pipe<[Event[]], Event[][], Event[][], Event[][]>(
    splitEventsByDeviceChange,
    filter<Event[]>(negate(isEmpty)),
    filter<Event[]>(
      (events) =>
        events[0].attributes.name !== EventName.InquirySessionStarted || events.length > 1,
    ),
  );

/**
 * Helper that does several things:
 * 1. subdivide them into a list of lists, split by days
 * 2. filter out empty lists
 */
export const getDaySegmentedEventTimelines = () =>
  pipe<[Event[]], Event[][], Event[][]>(splitEventsByDay, filter<Event[]>(negate(isEmpty)));

interface EventTimelineByDayProps {
  caseAssignmentPolicies: CaseAssignmentPolicy[];
  importerPipelineMap: Record<string, { id: string; name: string }>;
  events: Event[];
  workflowIdToNameMap: Record<string, string>;
  workflowRuns: WorkflowRun[];
  users: User[];
  location: EventTimelinePage;
}

const EventTimelineByDay = ({
  caseAssignmentPolicies,
  events,
  importerPipelineMap,
  location,
  users,
  workflowIdToNameMap,
  workflowRuns,
}: EventTimelineByDayProps) => {
  const date = events[0].attributes['created-at'];
  const segments = useMemo(() => getDeviceSegmentedEventTimelines()(events), [events]);

  return (
    <EventTimelineContainer>
      <MetadataContainer>
        <MetadataHeaderContainer>
          <MetadataHeader>{DateTime.fromISO(date).toFormat('DD')}</MetadataHeader>
        </MetadataHeaderContainer>
      </MetadataContainer>
      {segments.map((s, i) => (
        <EventTimeline
          key={i}
          caseAssignmentPolicies={caseAssignmentPolicies}
          importerPipelineMap={importerPipelineMap}
          events={s}
          users={users}
          workflowIdToNameMap={workflowIdToNameMap}
          location={location}
          workflowRuns={workflowRuns}
        />
      ))}
    </EventTimelineContainer>
  );
};

interface EventTimelineProps {
  caseAssignmentPolicies: CaseAssignmentPolicy[];
  importerPipelineMap: Record<string, { id: string; name: string }>;
  events: Event[];
  workflowIdToNameMap: Record<string, string>;
  workflowRuns: WorkflowRun[];
  users: User[];
}

const EventTimelineContainer = styled('div')`
  margin-top: ${Spacing[2]};
`;

const EventTimeline = ({
  caseAssignmentPolicies,
  events,
  importerPipelineMap,
  workflowIdToNameMap,
  workflowRuns,
  users,
}: EventTimelineProps) => (
  <EventTimelineContainer>
    <DeviceMetadata events={events} />
    {events.map((event, i) => (
      <EventItem
        key={event.id}
        caseAssignmentPolicies={caseAssignmentPolicies}
        importerPipelineMap={importerPipelineMap}
        event={event}
        events={events}
        users={users}
        workflowIdToNameMap={workflowIdToNameMap}
        workflowRuns={workflowRuns}
        isLast={i === events.length - 1}
      />
    ))}
  </EventTimelineContainer>
);

const MetadataContainer = styled('div')`
  position: relative;
  margin: 0 ${Spacing[4]} ${Spacing[2]};

  &:after {
    border-top: 1px solid ${({ theme }) => theme.color.border.primary};
    content: '';
    position: absolute;
    top: ${Spacing[3]};
    width: 100%;
  }
`;

const MetadataBody = styled('div')`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-row-gap: ${Spacing[2]};
`;

const MetadataHeaderContainer = styled('div')`
  display: flex;
`;

const MetadataHeader = styled('h4')`
  background: ${({ theme }) => theme.color.background.primary};
  display: flex;
  /* places item above divider line */
  z-index: 1;
  color: ${({ theme }) => theme.color.text.primary};
  padding-right: ${Spacing[3]};
`;

const MetadataIconContainer = styled('div')`
  margin-right: ${Spacing[2]};
  display: flex;
  align-items: center;

  > svg {
    height: 16px;
    width: 16px;
  }
`;

const MetadataDesc = styled('span')`
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  color: ${({ theme }) => theme.color.text.secondary};
`;

interface MetadataProps {
  events: Event[];
}

const DeviceMetadata = ({ events }: MetadataProps) => {
  if (events[0] == null || events[0].attributes.name !== EventName.InquirySessionStarted) {
    return null;
  }

  const timeElapsed = getTimeElapsed(
    events.map((e) => e.attributes['created-at']),
    MINUTES_SECONDS_ELAPSED_FORMAT,
  );

  const session = events[0].attributes.payload.data as InquirySession;
  const deviceId = session.relationships.device.data?.id;
  const device = deviceId
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (events[0].attributes.payload.included.find((obj) => obj.id === deviceId) as Device)
    : null;

  const {
    'browser-full-version': browserFullVersion,
    'browser-name': browserName,
    'device-name': deviceName,
    'device-type': deviceType,
    'os-full-version': osFullVersion,
    'os-name': osName,
  } = device?.attributes || {};

  const os = [osName, osFullVersion].filter(Boolean).join(' ');
  const browser = [browserName, browserFullVersion].filter(Boolean).join(' ');
  const metadataDesc = [browser, os, deviceType].filter(Boolean).join(', ');

  return (
    <MetadataContainer data-test={`event-timeline__meta--${session.id}`}>
      <MetadataBody>
        {device && (
          <MetadataHeaderContainer>
            <MetadataHeader>
              <MetadataIconContainer>
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                <DeviceIcon deviceType={deviceType} deviceName={deviceName} />
              </MetadataIconContainer>
              New Device (Duration: {timeElapsed})
            </MetadataHeader>
          </MetadataHeaderContainer>
        )}
        <MetadataDesc>{metadataDesc}</MetadataDesc>
      </MetadataBody>
    </MetadataContainer>
  );
};

const EventItemIconContainer = styled('div')`
  margin: 0 14px;
  position: relative;
`;

const EventItemIcon = styled('div')`
  width: ${Spacing[5]};
  display: inline-block;
  outline: 2px solid ${({ theme }) => theme.color.background.primary};
  position: relative;
  border-radius: ${Spacing[3]};
  background-color: ${({ theme }) => theme.color.background.primary};
  color: ${({ theme }) => theme.color.border.secondary};
  z-index: 1;

  svg {
    display: block; // removes space for decenders that svg's default inline has
    margin: auto; // center svg as a block element in parent component
  }
`;

const EventContainer = styled('div')`
  display: flex;
  justify-items: center;
  // align events with timeline date header
  padding: ${Spacing[3]} ${Spacing[3]} ${Spacing[3]} ${Spacing[4]};

  &:not(:last-child) {
    ${EventItemIconContainer}:after {
      content: '';
      position: absolute;
      top: ${Spacing[2]};
      height: 100%;
      border-left: 2px solid ${({ theme }) => theme.color.border.secondary};
      border-bottom-left-radius: ${BORDER_RADIUSES.base};
      border-bottom-right-radius: ${BORDER_RADIUSES.base};
      padding-bottom: ${Spacing[5]};
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const Timestamp = styled(Label).attrs(() => ({ size: 'md', color: 'secondary' }))`
  flex-shrink: 0;
  line-height: inherit;
  width: 80px;
`;

const EventItemDesc = styled('div')`
  color: ${({ theme }) => theme.color.text.primary};
  flex-grow: 1;
`;

interface EventItemProps {
  caseAssignmentPolicies: CaseAssignmentPolicy[];
  importerPipelineMap: Record<string, { id: string; name: string }>;
  event: Event;
  events: Event[];
  workflowIdToNameMap: Record<string, string>;
  workflowRuns: WorkflowRun[];
  users: User[];
  isLast: boolean;
}

const EventItem = ({
  caseAssignmentPolicies,
  importerPipelineMap,
  event,
  events,
  isLast,
  workflowIdToNameMap,
  workflowRuns,
  users,
}: EventItemProps) => {
  const { 'created-at': createdAt, payload, name } = event.attributes;

  const createdAtFormatted = DateTime.fromISO(createdAt).toFormat('tt');

  switch (name) {
    case EventName.DocumentCreated:
    case EventName.DocumentProcessed:
    case EventName.DocumentErrored: {
      const documentData = payload.data as Document;
      const documentStatus = documentData.attributes.status;

      const statusDesc = getDocumentStatusChangeName(documentStatus);
      if (statusDesc) {
        return (
          <EventContainer key={name} data-test='event-timeline__item'>
            <Timestamp>{createdAtFormatted}</Timestamp>
            <EventItemIconContainer>
              <EventItemIcon>
                <StatusChangeIcon status={documentStatus} isLast={isLast} />
              </EventItemIcon>
            </EventItemIconContainer>
            <EventItemDesc>{statusDesc}</EventItemDesc>
          </EventContainer>
        );
      } else {
        return null;
      }
    }

    case EventName.VerificationCreated:
    case EventName.VerificationSubmitted:
    case EventName.VerificationPassed:
    case EventName.VerificationRequiresRetry:
    case EventName.VerificationFailed:
    case EventName.VerificationCanceled:
    case EventName.VerificationTentativelyPassed:
    case EventName.VerificationTentativelyFailed: {
      const verificationData = payload.data as Verification;
      const verificationStatus = verificationData.attributes.status;
      const verificationType = verificationData.type;

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const statusDesc = getVerificationStatusChangeName(verificationType, verificationStatus);
      if (statusDesc) {
        return (
          <EventContainer key={name} data-test='event-timeline__item'>
            <Timestamp>{createdAtFormatted}</Timestamp>
            <EventItemIconContainer>
              <EventItemIcon>
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                <StatusChangeIcon status={verificationStatus} isLast={isLast} />
              </EventItemIcon>
            </EventItemIconContainer>
            <EventItemDesc>{statusDesc}</EventItemDesc>
          </EventContainer>
        );
      } else {
        return null;
      }
    }

    case EventName.InquiryCreated:
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{getEventDescription(name)}</EventItemDesc>
        </EventContainer>
      );

    case EventName.InquiryCompleted:
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <CircleCheckIcon
                color={Color.BorderIconSuccess}
                height={24}
                width={24}
                data-test='event-timeline__item--passed'
              />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{getEventDescription(name)}</EventItemDesc>
        </EventContainer>
      );

    case EventName.InquiryExpired:
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <CircleXIcon color={Color.BorderIconDanger} height={24} width={24} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{getEventDescription(name)}</EventItemDesc>
        </EventContainer>
      );

    case EventName.InquiryApproved:
    case EventName.InquiryDeclined:
    case EventName.InquiryMarkedForReview: {
      const inquiryData = event.attributes.payload.data as InquiryEvent;
      const description =
        inquiryData.attributes.reviewer != null
          ? actorDescription({
              preposition: 'by',
              actor: inquiryData.attributes.reviewer,
              actorType: inquiryData.attributes['reviewer-type'],
              comment: inquiryData.attributes['reviewer-comment'],
              workflowIdToNameMap,
              workflowRuns,
              users,
              events,
              caseAssignmentPolicies,
            })
          : inquiryData.relationships.reviewer?.data != null
            ? actorDescription({
                preposition: 'by',
                actor: inquiryData.relationships.reviewer.data.id,
                actorType: inquiryData.relationships.reviewer.data.type,
                comment: inquiryData.attributes['reviewer-comment'],
                workflowIdToNameMap,
                workflowRuns,
                users,
                events,
                caseAssignmentPolicies,
              })
            : null;
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>
              {getEventDescription(name)} {description}
            </>
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.CaseCreated: {
      const caseData = event.attributes.payload.data as CaseEvent;
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>
              {getCaseEventDescription(event)}{' '}
              {actorDescription({
                preposition: 'by',
                actor: caseData.attributes['creator-id'],
                actorType: caseData.attributes['creator-type'],
                workflowIdToNameMap,
                workflowRuns,
                users,
                events,
                caseAssignmentPolicies,
              })}
            </>
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.CaseAssigned: {
      const caseData = event.attributes.payload.data as CaseEvent;
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>
              {getCaseEventDescription(event)}{' '}
              {actorDescription({
                preposition: 'to',
                actor: caseData.attributes['assignee-id'],
                actorType: ApiModel.User,
                workflowIdToNameMap,
                workflowRuns,
                users,
                events,
                caseAssignmentPolicies,
                defaultIfEmpty: 'to Unassigned',
              })}{' '}
              {actorDescription({
                preposition: 'by',
                actor: caseData.attributes['assigner-id'] || caseData.attributes['updater-id'],
                actorType:
                  caseData.attributes['assigner-type'] || caseData.attributes['updater-type'],
                workflowIdToNameMap,
                workflowRuns,
                users,
                events,
                caseAssignmentPolicies,
                defaultIfEmpty: 'automatically',
              })}
            </>
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.CaseUpdated:
    case EventName.CaseStatusUpdated: {
      const caseData = event.attributes.payload.data as CaseEvent;
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>
              {getCaseEventDescription(event)}{' '}
              {actorDescription({
                preposition: 'by',
                actor: caseData.attributes['updater-id'],
                actorType: caseData.attributes['updater-type'],
                workflowIdToNameMap,
                workflowRuns,
                users,
                events,
                caseAssignmentPolicies,
              })}
            </>
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.CaseResolved: {
      const caseData = event.attributes.payload.data as CaseEvent;
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>
              {getCaseEventDescription(event)}{' '}
              {actorDescription({
                preposition: 'by',
                actor: caseData.attributes['resolver-id'],
                actorType: caseData.attributes['resolver-type'],
                workflowIdToNameMap,
                workflowRuns,
                users,
                events,
                caseAssignmentPolicies,
              })}
            </>
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.CaseReopened: {
      const caseData = event.attributes.payload.data as CaseEvent;
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>
              {getCaseEventDescription(event)}{' '}
              {actorDescription({
                preposition: 'by',
                actor: caseData.attributes['updater-id'],
                actorType: caseData.attributes['updater-type'],
                workflowIdToNameMap,
                workflowRuns,
                users,
                events,
                caseAssignmentPolicies,
              })}
            </>
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.CaseMessageSent: {
      const messageData = (event.attributes.payload.data as CaseMessage).attributes;
      // Older events only have sender-user-id and no sender-id nor sender-type
      // So we rely on sender-id and sender-type if it is present and fallback on sender-user-id
      const user = users.find(
        (user) => user.id === (messageData['sender-id'] ?? messageData['sender-user-id']),
      );
      const sender =
        messageData['sender-type'] === ApiModel.User || messageData['sender-user-id']
          ? userDisplayName(user)
          : actorDescription({
              preposition: '',
              actor: messageData['sender-id'],
              actorType: messageData['sender-type'],
              workflowIdToNameMap,
              workflowRuns,
              users,
              events,
              caseAssignmentPolicies,
            });

      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{DateTime.fromISO(messageData['sent-at']).toFormat('tt')}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>
              Email sent from {sender} &lt;{messageData.mail.from[0]}&gt; with subject:&nbsp;
              {dompurify.sanitize(messageData.mail.subject, { ALLOWED_TAGS: [] })}
            </>
          </EventItemDesc>
        </EventContainer>
      );
    }
    case EventName.CaseMessageReceived: {
      const messageData = (event.attributes.payload.data as CaseMessage).attributes;
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{DateTime.fromISO(messageData['sent-at']).toFormat('tt')}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>
              Email received from {messageData.mail.from[0]} with subject:&nbsp;
              {dompurify.sanitize(messageData.mail.subject, { ALLOWED_TAGS: [] })}
            </>
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.AccountRedacted:
    case EventName.ReportAddressLookupErrored:
    case EventName.ReportAddressLookupReady:
    case EventName.ReportAdverseMediaDismissed:
    case EventName.ReportAdverseMediaErrored:
    case EventName.ReportAdverseMediaMatched:
    case EventName.ReportAdverseMediaReady:
    case EventName.ReportBusinessAdverseMediaDismissed:
    case EventName.ReportBusinessAdverseMediaErrored:
    case EventName.ReportBusinessAdverseMediaMatched:
    case EventName.ReportBusinessAdverseMediaReady:
    case EventName.ReportBusinessAssociatedPersonsErrored:
    case EventName.ReportBusinessAssociatedPersonsMatched:
    case EventName.ReportBusinessAssociatedPersonsReady:
    case EventName.ReportBusinessClassificationErrored:
    case EventName.ReportBusinessClassificationReady:
    case EventName.ReportBusinessIndustryClassificationErrored:
    case EventName.ReportBusinessIndustryClassificationReady:
    case EventName.ReportBusinessIndustryClassificationSubmitted:
    case EventName.ReportBusinessLiensLookupErrored:
    case EventName.ReportBusinessLiensLookupReady:
    case EventName.ReportBusinessLookupErrored:
    case EventName.ReportBusinessLookupReady:
    case EventName.ReportBusinessNonprofitChanged:
    case EventName.ReportBusinessNonprofitErrored:
    case EventName.ReportBusinessNonprofitReady:
    case EventName.ReportBusinessOnlinePresenceErrored:
    case EventName.ReportBusinessOnlinePresenceReady:
    case EventName.ReportBusinessOnlinePresenceSubmitted:
    case EventName.ReportBusinessRegistrationsLookupErrored:
    case EventName.ReportBusinessRegistrationsLookupReady:
    case EventName.ReportBusinessSocialMediaErrored:
    case EventName.ReportBusinessSocialMediaReady:
    case EventName.ReportBusinessWatchlistDismissed:
    case EventName.ReportBusinessWatchlistErrored:
    case EventName.ReportBusinessWatchlistMatched:
    case EventName.ReportBusinessWatchlistReady:
    case EventName.ReportBusinessWebsiteErrored:
    case EventName.ReportBusinessWebsiteReady:
    case EventName.ReportClearbitBusinessLookupErrored:
    case EventName.ReportClearbitBusinessLookupReady:
    case EventName.ReportCryptoAddressWatchlistErrored:
    case EventName.ReportCryptoAddressWatchlistMatched:
    case EventName.ReportCryptoAddressWatchlistReady:
    case EventName.ReportCustomListDismissed:
    case EventName.ReportCustomListErrored:
    case EventName.ReportCustomListMatched:
    case EventName.ReportCustomListReady:
    case EventName.ReportEmailAddressErrored:
    case EventName.ReportEmailAddressReady:
    case EventName.ReportFinraBrokerCheckErrored:
    case EventName.ReportFinraBrokerCheckReady:
    case EventName.ReportKyckrBusinessLookupErrored:
    case EventName.ReportKyckrBusinessLookupReady:
    case EventName.ReportMiddeskErrored:
    case EventName.ReportMiddeskReady:
    case EventName.ReportMxAccountErrored:
    case EventName.ReportMxAccountReady:
    case EventName.ReportPhoneNumberErrored:
    case EventName.ReportPhoneNumberReady:
    case EventName.ReportPoliticallyExposedPersonDismissed:
    case EventName.ReportPoliticallyExposedPersonErrored:
    case EventName.ReportPoliticallyExposedPersonMatched:
    case EventName.ReportPoliticallyExposedPersonReady:
    case EventName.ReportProfileErrored:
    case EventName.ReportProfileReady:
    case EventName.ReportProfileNonAuthoritativeErrored:
    case EventName.ReportProfileNonAuthoritativeReady:
    case EventName.ReportScreeningErrored:
    case EventName.ReportScreeningReady:
    case EventName.ReportSecActionLookupErrored:
    case EventName.ReportSecActionLookupReady:
    case EventName.ReportSentilinkApplicationRiskErrored:
    case EventName.ReportSentilinkApplicationRiskReady:
    case EventName.ReportSentilinkScoresErrored:
    case EventName.ReportSentilinkScoresReady:
    case EventName.ReportSocialMediaErrored:
    case EventName.ReportSocialMediaReady:
    case EventName.ReportSyntheticErrored:
    case EventName.ReportSyntheticReady:
    case EventName.ReportWatchlistDismissed:
    case EventName.ReportWatchlistErrored:
    case EventName.ReportWatchlistMatched:
    case EventName.ReportWatchlistReady:
    case EventName.SARCreated:
    case EventName.SARFiled:
    case EventName.TransactionRedacted:
    case EventName.TransactionCreated:
    case EventName.TransactionLabeled: {
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>{getEventDescription(name)}</>
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.TransactionStatusUpdated: {
      const transactionData = payload.data as TransactionEvent;
      const statusDesc = EVENT_NAME_TO_DESCRIPTION[name];
      const description =
        transactionData.relationships.reviewer?.data != null
          ? actorDescription({
              preposition: 'by',
              actor: transactionData.relationships.reviewer.data.id,
              actorType: transactionData.relationships.reviewer.data.type,
              workflowIdToNameMap,
              workflowRuns,
              users,
              events,
              caseAssignmentPolicies,
            })
          : null;
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            {statusDesc} to {transactionData.attributes['status']} {description}
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.AccountStatusUpdated: {
      const accountData = payload.data as AccountEvent;
      const statusDesc = EVENT_NAME_TO_DESCRIPTION[name];
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            {statusDesc} to <Sticker>{accountData.attributes['account-status']}</Sticker>
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.WorkflowRunErrored: {
      const statusDesc = EVENT_NAME_TO_DESCRIPTION[name];
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <CircleXIcon color={Color.BorderIconDanger} height={24} width={24} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{statusDesc} </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.WorkflowRunResumed: {
      const statusDesc = EVENT_NAME_TO_DESCRIPTION[name];
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{statusDesc} </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.WorkflowRunCreated: {
      const workflowRunData = event.attributes.payload.data as WorkflowRunEvent;
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>
              {getWorkflowRunEventDescription(event, workflowRuns, workflowIdToNameMap)}{' '}
              {actorDescription({
                preposition: 'by',
                actor: workflowRunData.relationships.creator?.data?.id,
                actorType: workflowRunData.relationships.creator?.data?.type,
                workflowIdToNameMap,
                workflowRuns,
                users,
                events,
                caseAssignmentPolicies,
              })}
            </>
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.AccountCreated:
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{getEventDescription(name)}</EventItemDesc>
        </EventContainer>
      );

    case EventName.AccountUpdatedFields: {
      const accountEventData = event.attributes.payload.data as AccountEvent;
      const eventContext = event.attributes?.context;
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <div>
              {getEventDescription(name)}{' '}
              {actorDescription({
                preposition: 'by',
                actor: eventContext?.['updater-id'],
                actorType: eventContext?.['updater-type']?.toLowerCase(),
                workflowIdToNameMap,
                workflowRuns,
                users,
                events,
                caseAssignmentPolicies,
                importerPipelineMap,
              })}
            </div>
            <div>
              <UpdatedFieldsEventDescription
                eventId={event.id}
                eventPayload={accountEventData}
                fieldsUpdated={eventContext?.['updated-fields'] ?? []}
              />
            </div>
          </EventItemDesc>
        </EventContainer>
      );
    }

    // TODO: add values for these if we ever want to display in the timeline
    case EventName.AccountArchived:
    case EventName.AccountRestored:
    case EventName.AccountConsolidated:
    case EventName.AccountTagAdded:
    case EventName.AccountTagRemoved:
    case EventName.CaseQueueUpdated:
    case EventName.CaseSlaExpired:
    case EventName.ExternalIntegrationLogCreated:
    case EventName.GraphQueryCancelled:
    case EventName.GraphQueryCompleted:
    case EventName.GraphQueryErrored:
    case EventName.GraphQueryReran:
    case EventName.GraphQuerySubmitted:
    case EventName.GraphQueryTimedOut:
    case EventName.InquiryFailed:
    case EventName.InquiryStarted:
    case EventName.InquiryTransitioned:
    case EventName.InquiryRedacted:
    case EventName.InquiryReusablePersonaCreated:
    case EventName.InquiryReusablePersonaUsed:
    case EventName.InquirySessionStarted:
    case EventName.InquirySessionExpired:
    case EventName.InquirySessionCanceled:
    case EventName.InquiryRunConditionalRouted:
    case EventName.InquiryTemplateVersionCreated:
    case EventName.InquiryTemplateVersionPublished:
    case EventName.DocumentSubmitted:
    case EventName.DocumentExtracted:
    case EventName.SelfieCreated:
    case EventName.SelfieSubmitted:
    case EventName.SelfieProcessed:
    case EventName.SelfieErrored:
    case EventName.SessionCreated:
    case EventName.SessionExpired:
      return null;

    default:
      assertNever(name);
  }
};

interface ActorDescriptionArgs {
  preposition: string;
  actor: string | undefined;
  actorType:
    | ApiModel.CaseAssignmentPolicy
    | ApiModel.User
    | ApiModel.WorkflowRun
    | ApiModel.Workflow
    | ApiModel.Event
    | string
    | undefined;
  comment?: string | undefined;
  workflowIdToNameMap: Record<string, string>;
  workflowRuns: WorkflowRun[];
  users: User[];
  events: Event[];
  caseAssignmentPolicies: CaseAssignmentPolicy[];
  importerPipelineMap?: Record<string, { id: string; name: string }>;
  defaultIfEmpty?: string;
}
const actorDescription = ({
  preposition,
  actor,
  actorType,
  comment,
  workflowIdToNameMap,
  workflowRuns,
  users,
  events,
  caseAssignmentPolicies,
  importerPipelineMap = {},
  defaultIfEmpty,
}: ActorDescriptionArgs) => {
  if (actor == null || actorType == null) {
    return `${defaultIfEmpty ? defaultIfEmpty : ''}`;
  }

  const withComment = comment ? `with comment "${comment}"` : '';

  switch (actorType) {
    case ApiModel.User: {
      if (actor.startsWith('user_')) {
        const user = users.find((user) => user.id === actor);
        return user != null
          ? [preposition, user.attributes['email-address'], withComment].filter(Boolean).join(' ')
          : null;
      } else {
        return [preposition, actor, withComment].filter(Boolean).join(' ');
      }
    }

    case ApiModel.WorkflowRun: {
      const workflowRun = workflowRuns.find((run) => run.id === actor);
      if (workflowRun == null) {
        return null;
      }
      const workflowId = workflowRun.relationships.workflow.data.id;
      const workflowName = workflowIdToNameMap[workflowId];
      let linkComponent;
      if (workflowRun.attributes['run-type'] === WorkflowRunType.Case) {
        const link = `/dashboard/case-actions/${workflowId}/runs/${actor}`;
        linkComponent = <Link to={link}>Case Action: {workflowName}</Link>;
      } else if (
        workflowRun.attributes['run-type'] === WorkflowRunType.Direct &&
        workflowRun.relationships['trigger-object']?.data.type === ApiModel.Inquiry
      ) {
        const inquiryId = workflowRun.relationships['trigger-object']?.data.id;
        const link = `/dashboard/inquiries/${inquiryId}`;
        linkComponent = <Link to={link}>Inquiry: {inquiryId}</Link>;
      } else {
        const link = `/dashboard/workflows/${workflowId}/runs/${actor}`;
        linkComponent = <Link to={link}>Workflow: {workflowName}</Link>;
      }

      return (
        <>
          <span>{preposition} </span>
          {linkComponent}
          {withComment && <span> {withComment}</span>}
        </>
      );
    }

    case ApiModel.Workflow: {
      const workflowName = workflowIdToNameMap[actor];
      const workflowLink = `/dashboard/workflows/${actor}`;

      return (
        <>
          <span>{preposition} </span>
          <Link to={workflowLink}>Workflow: {workflowName}</Link>
          {withComment && <span> {withComment}</span>}
        </>
      );
    }

    case ApiModel.Event: {
      const event = events.find((e) => e.id === actor);
      if (event == null) {
        return null;
      }
      const eventLink = `/dashboard/events/${event.id}`;

      return (
        <>
          <span>{preposition}</span>
          <Link to={eventLink}> Event: {event.attributes.name}</Link>
          {withComment && <span> {withComment}</span>}
        </>
      );
    }

    case ApiModel.CaseAssignmentPolicy: {
      const ap = caseAssignmentPolicies.find((ap) => ap.id === actor);
      if (ap == null) {
        return null;
      }
      return (
        <>
          <span>{preposition} </span>
          <Link to={`/dashboard/case-assignment-policies/${ap.id}`}>
            Case Assignment Policy: {ap.attributes.name}
          </Link>
          {withComment && <span> {withComment}</span>}
        </>
      );
    }

    case ApiModel.Inquiry: {
      const inquiryLink = `/dashboard/inquiries/${actor}`;

      return (
        <>
          <span>{preposition} </span>
          <Link to={inquiryLink}>Inquiry: {actor}</Link>
          {withComment && <span> {withComment}</span>}
        </>
      );
    }

    case ApiModel.ImporterRun: {
      const importerPipelineId = importerPipelineMap[actor].id;
      if (importerPipelineId == null) {
        return null;
      }
      return (
        <>
          <span>{preposition} </span>
          <Link to={`/dashboard/imports/importers/${importerPipelineId}/${actor}/run-details`}>
            Importer Pipeline: {importerPipelineMap[actor].name || importerPipelineId}
          </Link>
        </>
      );
    }
  }
};

const StatusChangeIcon = ({
  status,
  isLast,
}: {
  status: DocumentStatus | VerificationStatus;
  isLast: boolean;
}) => {
  switch (status) {
    case DocumentStatus.Initiated:
    case VerificationStatus.Initiated:
    case VerificationStatus.Confirmed:
      return <ImageDot height={12} width={12} />;

    case VerificationStatus.Submitted:
      return isLast ? (
        <CircleMinusIcon color={Color.BorderIconWarning} height={24} width={24} />
      ) : (
        <ImageDot height={12} width={12} />
      );

    case DocumentStatus.Processed:
    case VerificationStatus.Passed:
      return (
        <CircleCheckIcon
          color={Color.BorderIconSuccess}
          height={24}
          width={24}
          data-test='event-timeline__item--passed'
        />
      );

    case DocumentStatus.Errored:
    case VerificationStatus.RequiresRetry:
    case VerificationStatus.Failed:
    case VerificationStatus.Canceled:
      return <CircleXIcon color={Color.BorderIconDanger} height={24} width={24} />;

    case VerificationStatus.TentativelyPassed:
    case VerificationStatus.TentativelyFailed:
    case VerificationStatus.Skipped:
      return <CircleMinusIcon color={Color.BorderIconWarning} height={24} width={24} />;

    case DocumentStatus.Submitted:
      return null;

    default:
      assertNever(status);
  }
};

const getDocumentStatusChangeName = (status: DocumentStatus) => {
  switch (status) {
    case DocumentStatus.Initiated:
      return 'Document started';

    case DocumentStatus.Processed:
      return 'Document uploaded';

    case DocumentStatus.Errored:
      return 'Document errored';

    case DocumentStatus.Submitted:
      return null;

    default:
      assertNever(status);
  }
};

const getVerificationStatusChangeName = (type: ApiModel, status: VerificationStatus) => {
  const verificationName = VERIFICATION_TYPE_NAMES[type as Verification['type']];

  switch (status) {
    case VerificationStatus.Initiated:
      return `${verificationName} started`;

    case VerificationStatus.Confirmed:
      return `${verificationName} confirmed`;

    case VerificationStatus.Submitted:
      return `${verificationName} submitted`;

    case VerificationStatus.TentativelyPassed:
      return `${verificationName} tentatively passed`;

    case VerificationStatus.TentativelyFailed:
      return `${verificationName} tentatively failed`;

    case VerificationStatus.Passed:
      return `${verificationName} passed`;

    case VerificationStatus.RequiresRetry:
      return `${verificationName} requires retry`;

    case VerificationStatus.Failed:
      return `${verificationName} failed`;

    case VerificationStatus.Canceled:
      return `${verificationName} canceled`;

    case VerificationStatus.Skipped:
      return `${verificationName} skipped`;

    default:
      assertNever(status);
  }
};

const getEventDescription = (name: EventName) => {
  return EVENT_NAME_TO_DESCRIPTION[name];
};

const getCaseEventDescription = (event: Event) => {
  switch (event.attributes.name) {
    case EventName.CaseCreated:
      return 'Case created';
    case EventName.CaseAssigned:
      return 'Case assigned';
    case EventName.CaseUpdated:
    case EventName.CaseStatusUpdated: {
      const caseData = event.attributes.payload.data as CaseEvent;
      return (
        <>
          Case marked as <Sticker>{caseData.attributes.status}</Sticker>
        </>
      );
    }
    case EventName.CaseResolved: {
      const caseData = event.attributes.payload.data as CaseEvent;
      return (
        <>
          Case resolved as{' '}
          <Sticker>{caseData.attributes.resolution ?? caseData.attributes.status}</Sticker>
        </>
      );
    }
    case EventName.CaseReopened:
      return 'Case reopened';
  }
};

const getWorkflowRunEventDescription = (
  event: Event,
  workflowRuns: WorkflowRun[],
  workflowIdToNameMap: Record<string, string>,
) => {
  const workflowRun = workflowRuns.find((run) => run.id === event.attributes.payload.data.id);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const workflowId = workflowRun.relationships.workflow.data.id;
  const workflowName = workflowIdToNameMap[workflowId];
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const isCaseAction = workflowRun.attributes['run-type'] === WorkflowRunType.Case;
  const workflowRunLink = `/dashboard/${isCaseAction ? 'case-actions' : 'workflows'}/${
    workflowId
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  }/runs/${workflowRun.id}`;
  const isManualRun = event.attributes?.context?.['is-manual-run'];
  const triggerCopy = isManualRun ? 'manually triggered' : 'triggered';

  if (isCaseAction) {
    return (
      <>
        <Link to={workflowRunLink}>Case Action: {workflowName}</Link>
        <span> {triggerCopy}</span>
      </>
    );
  } else {
    return (
      <>
        <Link to={workflowRunLink}>Workflow: {workflowName}</Link>
        <span> {triggerCopy}</span>
      </>
    );
  }
};
